package top.wcpe.allincore.dsl.compiler.diag

/**
 * DSL 编译期所有诊断码的集中定义。
 *
 * 设计目标：
 * - 全集集中：新增/重命名错误码须改本文件，避免散落字面量
 * - 防重号：enum 同名常量在编译期冲突
 * - 模板化：消息模板统一在此处维护，调用点仅传参数
 * - 测试友好：测试断言用 [DslError.E306_PROPS_REF_UNDECLARED.code] 而非裸字符串 "DSL_E306"
 *
 * 模板风格遵循 MessageFormat 的平凡子集：占位符为 `{0}` `{1}`，单引号写作 `''`，
 * 字面花括号写作 `'{'` `'}'`（无 `{N,number}` 格式子句——由 [formatTemplate] 平台中立实现
 * 填充，FR-UI-061 起 JVM/JS 双端逐字符一致）。
 * 同一错误码若被多处不同文案使用，模板使用通用占位 `{0}`，由调用点拼好完整消息后传入。
 */
enum class DslError(
    val code: String,
    val template: String,
    val category: Category,
    /** M5-f：可选的修复建议，向用户提示如何修正该错误。 */
    val fixHint: String? = null,
) {
    // ===== E0xx 词法 / 解析顶层 =====

    /** 词法阶段抛出的 LexException 转报码。message 由 LexException 透传。 */
    E001_LEX_ERROR("DSL_E001", "{0}", Category.LEX),

    /** 解析阶段抛出的 ParseException 转报码。message 由 ParseException 透传。 */
    E002_PARSE_ERROR("DSL_E002", "{0}", Category.PARSE),

    /** 组件 / 主题源文件顶层必须为 component / theme，但实际写成了别的（如 screen）。 */
    E003_WRONG_TOPLEVEL_KIND(
        "DSL_E003",
        "源文件 ''{0}'' 期望顶层为 {1}，但实际是 {2}",
        Category.PARSE,
        fixHint = "组件文件请用 `component { props { ... } root { ... } }`；" +
            "主题文件请用 `theme { scheme = ... }`；如果想写界面请放到 screens 目录而不是 components 目录。",
    ),

    /** 三引号字符串未闭合。 */
    E021_UNTERMINATED_TRIPLE_STRING("DSL_E021", "三引号字符串未闭合", Category.LEX),

    /** 三引号字符串内嵌套字面量。 */
    E022_NESTED_TRIPLE_STRING_LITERAL("DSL_E022", "三引号字符串内嵌套字面量", Category.LEX),

    // ===== E1xx 顶层重复声明 =====

    /** state 字段重复声明。 */
    E101_DUPLICATE_STATE_FIELD("DSL_E101", "state 字段 ''{0}'' 重复声明", Category.SEMANTIC),

    /** action 重复声明。 */
    E102_DUPLICATE_ACTION("DSL_E102", "action ''{0}'' 重复声明", Category.SEMANTIC),

    /** animation 重复声明。 */
    E103_DUPLICATE_ANIMATION("DSL_E103", "animation ''{0}'' 重复声明", Category.SEMANTIC),

    /** fun 重复声明。 */
    E104_DUPLICATE_FUN("DSL_E104", "fun ''{0}'' 重复声明", Category.SEMANTIC),

    // ===== E2xx 引用解析 =====

    /** 引用了未声明的 state 字段。 */
    E201_UNDECLARED_STATE("DSL_E201", "未声明的 state 字段 ''{0}''", Category.SEMANTIC),

    /** 引用了未声明的 action / 事件入口。多种文案，调用点自填完整消息。 */
    E202_UNDECLARED_ACTION("DSL_E202", "{0}", Category.SEMANTIC),

    /** 引用了未声明的 animation。 */
    E203_UNDECLARED_ANIMATION("DSL_E203", "未声明的 animation ''{0}''", Category.SEMANTIC),

    /** derived 派生值之间存在循环依赖（如 a → b → a）。 */
    E204_DERIVED_CYCLE(
        "DSL_E204",
        "derived 派生值存在循环依赖：{0}",
        Category.SEMANTIC,
        fixHint = "derived 表达式不能直接或间接引用自身；请打破依赖环。",
    ),

    /** derived 派生表达式内出现禁止的语义（如 await）。 */
    E205_DERIVED_FORBIDDEN_EXPR(
        "DSL_E205",
        "derived 派生表达式不支持 {0}",
        Category.SEMANTIC,
        fixHint = "derived 仅用于纯派生计算，不能包含 await 等异步 / 副作用操作。",
    ),

    // ===== E3xx 组件 / props =====

    /** 调用了未注册的组件。 */
    E300_COMPONENT_UNREGISTERED("DSL_E300", "未定义的组件 ''{0}''", Category.COMPONENT),

    /**
     * 字面量类型校验失败 / 事件 prop 右值非合法 fun 引用。
     * 多种文案，调用点自填完整消息。
     */
    E301_LITERAL_TYPE_MISMATCH("DSL_E301", "{0}", Category.COMPONENT),

    /**
     * 调用方传递了组件未声明的 prop / 动画 prop 不是字符串 id。
     * 多种文案，调用点自填完整消息。
     */
    E302_UNKNOWN_PROP("DSL_E302", "{0}", Category.COMPONENT),

    /**
     * 组件展开递归深度超限 / 动画属性必须为字面量。
     * 多种文案，调用点自填完整消息。
     */
    E303_RECURSION_DEPTH_EXCEEDED("DSL_E303", "{0}", Category.COMPONENT),

    /** 单个组件 root 树内存在 2+ 个 children 占位。 */
    E304_MULTIPLE_CHILDREN_SLOTS(
        "DSL_E304",
        "组件 ''{0}'' 内存在 {1} 个 children 占位，至多允许 1 个",
        Category.COMPONENT,
    ),

    /**
     * children 关键字仅可出现在 component 文件内 / 占位不允许带 id / 属性 / 子块。
     * 多种文案，调用点自填完整消息。
     */
    E305_CHILDREN_MISUSE("DSL_E305", "{0}", Category.PARSE),

    /** 组件 root 引用了未声明的 prop。 */
    E306_PROPS_REF_UNDECLARED(
        "DSL_E306",
        "组件 ''{0}'' 的 root 引用了未声明的 prop ''{1}''",
        Category.COMPONENT,
    ),

    /** 同名组件已存在。 */
    E308_DUPLICATE_COMPONENT("DSL_E308", "组件名 ''{0}'' 重复注册", Category.COMPONENT),

    /** 必传 prop 缺失。 */
    E309_REQUIRED_PROP_MISSING(
        "DSL_E309",
        "组件 ''{0}'' 的必传 prop ''{1}'' 未提供",
        Category.COMPONENT,
    ),

    /** prop 默认值字面量类型不匹配。 */
    E310_DEFAULT_VALUE_TYPE_MISMATCH(
        "DSL_E310",
        "prop ''{0}'' 默认值字面量与类型 {1} 不匹配",
        Category.PARSE,
    ),

    /**
     * PropsRef 应被组件展开消除（防御围栏）。
     * 文案较长且固定，调用点自填完整消息。
     */
    E311_PROPS_REF_LEAKED("DSL_E311", "{0}", Category.COMPONENT),

    // ----- slotGrid 静态展开（B2） -----

    /** slotGrid 缺少 source 属性。 */
    E312_SLOTGRID_SOURCE_MISSING(
        "DSL_E312",
        "slotGrid ''{0}'' 缺少必传属性 source",
        Category.COMPONENT,
    ),

    /** slotGrid 缺少 cols 属性。 */
    E313_SLOTGRID_COLS_MISSING(
        "DSL_E313",
        "slotGrid ''{0}'' 缺少必传属性 cols",
        Category.COMPONENT,
    ),

    /** slotGrid 缺少 rows 属性（dynamic=false 时必传）。 */
    E314_SLOTGRID_ROWS_MISSING(
        "DSL_E314",
        "slotGrid ''{0}'' 缺少必传属性 rows",
        Category.COMPONENT,
    ),

    /** slotGrid cols 取值非法（必须为正整数字面量）。 */
    E315_SLOTGRID_COLS_INVALID(
        "DSL_E315",
        "slotGrid ''{0}'' 的 cols 必须为正整数字面量",
        Category.COMPONENT,
    ),

    /** slotGrid rows 取值非法（必须为正整数字面量）。 */
    E316_SLOTGRID_ROWS_INVALID(
        "DSL_E316",
        "slotGrid ''{0}'' 的 rows 必须为正整数字面量",
        Category.COMPONENT,
    ),

    /** slotGrid indexOrder 枚举值未知。 */
    E317_SLOTGRID_INDEX_ORDER_UNKNOWN(
        "DSL_E317",
        "slotGrid ''{0}'' 的 indexOrder 取值 ''{1}'' 未知，可选 rowMajor / colMajor / reverseRowMajor",
        Category.COMPONENT,
    ),

    // ----- inventory 静态展开（B3） -----

    /** inventory 缺少 source 属性。 */
    E318_INVENTORY_SOURCE_MISSING(
        "DSL_E318",
        "inventory ''{0}'' 缺少必传属性 source",
        Category.COMPONENT,
    ),

    /** inventory 缺少 direction 属性。 */
    E319_INVENTORY_DIRECTION_MISSING(
        "DSL_E319",
        "inventory ''{0}'' 缺少必传属性 direction",
        Category.COMPONENT,
    ),

    /** inventory direction 取值未知。 */
    E320_INVENTORY_DIRECTION_UNKNOWN(
        "DSL_E320",
        "inventory ''{0}'' 的 direction 取值 ''{1}'' 未知，可选 horizontal / vertical",
        Category.COMPONENT,
    ),

    /** inventory 未提供 count 且 dynamic 不为 true。 */
    E321_INVENTORY_COUNT_REQUIRED(
        "DSL_E321",
        "inventory ''{0}'' 必须显式声明 count 或设置 dynamic=true",
        Category.COMPONENT,
    ),

    /** inventory count 取值非法（必须为正整数字面量）。 */
    E322_INVENTORY_COUNT_INVALID(
        "DSL_E322",
        "inventory ''{0}'' 的 count 必须为正整数字面量",
        Category.COMPONENT,
    ),

    // ----- 具名插槽（named slots） -----
    //
    // 计划文案原拟用 E320 / E321，但这两个码号已被 inventory 静态展开占用（见上）；
    // 此处沿用计划的语义命名（DUPLICATE_NAMED_SLOT / FILL_UNDECLARED_SLOT），改用空闲码号 E323 / E324。

    /** 同一组件 root 内重复声明同名具名插槽（`children "header"` 出现多次）。 */
    E323_DUPLICATE_NAMED_SLOT(
        "DSL_E323",
        "组件 ''{0}'' 内具名插槽 ''{1}'' 重复声明 {2} 次，同名插槽至多允许 1 个",
        Category.COMPONENT,
    ),

    /** 调用方填充了组件未声明的具名插槽（`slot "x" {}` 中 x 在组件 root 内无对应 `children "x"`）。 */
    E324_FILL_UNDECLARED_SLOT(
        "DSL_E324",
        "组件 ''{0}'' 未声明具名插槽 ''{1}''，无法填充",
        Category.COMPONENT,
    ),

    /**
     * 组件块内出现了不支持的状态 / 生命周期块。
     *
     * 计划 04 决策点 D：组件内仅支持 `state defaults { ... }`，
     * session / persist / derived / fun / open / close 等一律不支持并显式报错（不静默吞）。
     */
    E325_COMPONENT_UNSUPPORTED_BLOCK(
        "DSL_E325",
        "组件块内仅支持 state，不支持 ''{0}''",
        Category.COMPONENT,
    ),

    /** 裸名组件在多个路径下定义，引用有歧义，需用 import 别名消歧（ADR-0038）。 */
    E326_AMBIGUOUS_COMPONENT_NAME(
        "DSL_E326",
        "组件名 ''{0}'' 有多处定义（{1}），请用 import 别名消歧",
        Category.COMPONENT,
    ),

    /** import 引用的组件路径在注册表中不存在（ADR-0038）。 */
    E327_IMPORT_TARGET_NOT_FOUND(
        "DSL_E327",
        "import 的组件路径 ''{0}'' 不存在",
        Category.COMPONENT,
    ),

    /** 同一文件内 import 别名重复声明（ADR-0038）。 */
    E328_DUPLICATE_IMPORT_ALIAS(
        "DSL_E328",
        "import 别名 ''{0}'' 在本文件内重复声明",
        Category.COMPONENT,
    ),

    // ===== E4xx 元素结构 =====

    /** 未知 element 类型。 */
    E401_UNKNOWN_KIND("DSL_E401", "未知元素类型 ''{0}''", Category.SEMANTIC),

    /** 元素 id 不合法。 */
    E402_ILLEGAL_ID("DSL_E402", "非法元素 id ''{0}''", Category.SEMANTIC),

    /** 同级元素 id 重复。 */
    E403_DUPLICATE_SIBLING_ID("DSL_E403", "同级元素 id ''{0}'' 重复", Category.SEMANTIC),

    /** Button variant 字符串字面量取值未知。 */
    E404_BUTTON_VARIANT_UNKNOWN(
        "DSL_E404",
        "button variant 取值 ''{0}'' 未知，可选 filled / tonal / outlined / text / elevated",
        Category.SEMANTIC,
        fixHint = "请使用 filled / tonal / outlined / text / elevated 之一",
    ),

    /** Text typography 字符串字面量取值未知。 */
    E405_TEXT_TYPOGRAPHY_UNKNOWN(
        "DSL_E405",
        "text typography 取值 ''{0}'' 未知，可选 displayLarge / displayMedium / displaySmall / headlineLarge / headlineMedium / headlineSmall / titleLarge / titleMedium / titleSmall / bodyLarge / bodyMedium / bodySmall / labelLarge / labelMedium / labelSmall",
        Category.SEMANTIC,
        fixHint = "请使用 Material typography 15 个角色之一，例如 bodyMedium",
    ),

    /** modelPreview 声明了非固定专有属性或非既有通用属性。 */
    E406_MODEL_PREVIEW_UNKNOWN_PROP(
        "DSL_E406",
        "modelPreview 不支持属性 ''{0}''",
        Category.SEMANTIC,
    ),

    /** modelPreview 缺少必填 source。 */
    E407_MODEL_PREVIEW_SOURCE_REQUIRED(
        "DSL_E407",
        "modelPreview ''{0}'' 必须声明 source 属性",
        Category.SEMANTIC,
    ),

    /** modelPreview 属性静态可知类型不匹配。 */
    E408_MODEL_PREVIEW_PROP_TYPE_MISMATCH(
        "DSL_E408",
        "modelPreview 属性 ''{0}'' 期望 {1}，实际为 {2}",
        Category.SEMANTIC,
    ),

    /** modelPreview source 静态路径非法。 */
    E409_MODEL_PREVIEW_SOURCE_PATH_INVALID(
        "DSL_E409",
        "modelPreview source 必须是非空相对 .bbmodel 路径，实际为 ''{0}''",
        Category.SEMANTIC,
    ),

    /** modelPreview texture 静态路径非法。 */
    E410_MODEL_PREVIEW_TEXTURE_PATH_INVALID(
        "DSL_E410",
        "modelPreview texture 必须是非空相对 png / jpeg / jpg / webp 路径，实际为 ''{0}''",
        Category.SEMANTIC,
    ),

    /** modelPreview scale 静态数值非法。 */
    E411_MODEL_PREVIEW_SCALE_INVALID(
        "DSL_E411",
        "modelPreview scale 必须是有限且大于零的 Number，实际为 {0}",
        Category.SEMANTIC,
    ),

    /** modelPreview rotationX / rotationY 静态数值非法。 */
    E412_MODEL_PREVIEW_ROTATION_INVALID(
        "DSL_E412",
        "modelPreview 属性 ''{0}'' 必须是有限 Number，实际为 {1}",
        Category.SEMANTIC,
    ),

    /** modelPreview 内部 base 仅允许编译器注入。 */
    E413_MODEL_PREVIEW_BASE_RESERVED(
        "DSL_E413",
        "modelPreview 内部属性 ''{0}'' 为编译器保留，作者不可声明",
        Category.SEMANTIC,
    ),

    // ===== E5xx 动画属性归一化 / 应用 =====

    /**
     * 元素属性归一化失败 / 动画字段非负 / keyframe offset 越界。
     * 多种文案，调用点自填完整消息。
     */
    E501_PROP_NORMALIZE_FAILED("DSL_E501", "{0}", Category.SEMANTIC),

    /** 元素属性应用失败 / 不支持的 easing。 */
    E502_PROP_APPLY_FAILED("DSL_E502", "不支持的 easing ''{0}''", Category.SEMANTIC),

    /** 同一 transition 同时声明 easing 与 spring（二者互斥，驱动方式冲突）。 */
    E503_ANIMATION_EASING_SPRING_CONFLICT(
        "DSL_E503",
        "同一动画节点不能同时声明 easing 与 spring（二者是互斥的驱动方式）",
        Category.SEMANTIC,
        fixHint = "spring 用物理弹簧驱动、easing 用 duration+曲线采样；请二选一，删除其中之一",
    ),

    /** spring 参数非法（stiffness / mass 必须为正，damping 必须非负）。 */
    E504_ANIMATION_SPRING_PARAM_INVALID(
        "DSL_E504",
        "spring 参数 ''{0}'' 非法：{1}",
        Category.SEMANTIC,
        fixHint = "stiffness / mass 必须为正数，damping 必须非负",
    ),

    // ===== D2 预留（D0 阶段先定义，runtime 暂不消费） =====

    /** try 体内不允许 await（第一版限制）。 */
    E601_AWAIT_IN_TRY(
        "DSL_E601",
        "try 体内不允许 await（第一版限制）",
        Category.SEMANTIC,
    ),

    /** var 推断类型与赋值类型不兼容。 */
    E602_VAR_TYPE_MISMATCH(
        "DSL_E602",
        "var ''{0}'' 推断类型为 {1}，赋值为不兼容的 {2}",
        Category.SEMANTIC,
    ),

    /** await net.request(...) 不允许携带 callback。 */
    E603_AWAIT_HAS_CALLBACK(
        "DSL_E603",
        "await net.request(...) 不允许携带 callback；await 已自动接管 success/failure/timeout 续延",
        Category.SEMANTIC,
    ),

    /**
     * `var x: {0} = await ...payload.{1}(...)` 中后缀链 typed helper
     * 返回类型与变量声明 / 期望类型不兼容。D2-a 仅定义，D2-b 才消费。
     */
    E604_AWAIT_TAIL_TYPE_MISMATCH(
        "DSL_E604",
        "var ''{0}'' = await ...payload.{1}(...) 类型与期望 {2} 不兼容",
        Category.SEMANTIC,
    ),

    /**
     * await 出现在不支持的表达式位置（return / 赋值 rhs / 调用实参 / 复合表达式等）。
     * 当前 lowerer 仅支持 `var x = await ...` / 裸 `await ...` 两种语句形态。
     */
    E605_AWAIT_IN_EXPR_POSITION(
        "DSL_E605",
        "await 仅允许出现在 `var x = await ...` 或裸 `await ...` 语句位置；不支持作为表达式嵌入 ({0})",
        Category.SEMANTIC,
    ),

    // ===== M4 A2 Stage 1：suspend lambda 支持 =====

    /** await 目标既不是 net.request(...) 也不是普通调用形态。 */
    E606_AWAIT_REQUIRES_SUSPEND_TARGET(
        "DSL_E606",
        "await 仅支持 net.request(...) 或 suspend lambda 调用，实际目标：{0}",
        Category.SEMANTIC,
    ),

    /** suspend lambda 被同步调用（未使用 await），body 含异步 await 时无法取返回值。 */
    E607_SUSPEND_LAMBDA_NOT_AWAITED(
        "DSL_E607",
        "suspend lambda ''{0}'' 必须用 await 调用（其 body 含异步 await，同步调用无法获取返回值）",
        Category.SEMANTIC,
    ),

    /** suspend lambda body 末位不是表达式语句，无法构造异步返回值。 */
    E608_SUSPEND_LAMBDA_PARTIAL_RETURN(
        "DSL_E608",
        "suspend lambda body 末位必须是表达式语句（其值作为异步返回值），实际：{0}",
        Category.SEMANTIC,
    ),

    /** batch 块内禁止出现 await（会引入异步边界，破坏合批的同步语义）。 */
    E609_AWAIT_IN_BATCH(
        "DSL_E609",
        "batch 块内禁止出现 await：合批要求同步执行，await 会切断执行流",
        Category.SEMANTIC,
        fixHint = "把 await 移到 batch 块外，或去掉 batch 包裹。",
    ),

    /**
     * `_` 是丢弃约定的形参占位符，不允许在函数体内被引用。
     */
    E611_DISCARD_PARAM_REFERENCED(
        "DSL_E611",
        "''_'' 是丢弃占位符，不能在函数体内被引用",
        Category.SEMANTIC,
        fixHint = "将 _ 替换为有意义的参数名，或删除对它的引用",
    ),

    // ===== M5-b：命名实参 + 默认实参 =====

    /** 命名实参后又出现了位置实参。 */
    E612_NAMED_ARG_BEFORE_POSITIONAL(
        "DSL_E612",
        "命名实参后不允许再出现位置实参",
        Category.PARSE,
        fixHint = "将所有命名实参移到位置实参之后",
    ),

    /** 同名命名实参重复。 */
    E613_DUPLICATE_NAMED_ARG(
        "DSL_E613",
        "命名实参 ''{0}'' 重复",
        Category.PARSE,
    ),

    /** 命名实参名不在形参列表中。 */
    E614_UNKNOWN_NAMED_ARG(
        "DSL_E614",
        "未知的命名实参 ''{0}''",
        Category.SEMANTIC,
    ),

    /** 必填形参未提供。 */
    E615_MISSING_REQUIRED_ARG(
        "DSL_E615",
        "必填形参 ''{0}'' 未提供",
        Category.SEMANTIC,
    ),

    /** 必填形参出现在默认实参之后。 */
    E616_REQUIRED_AFTER_DEFAULT(
        "DSL_E616",
        "必填形参 ''{0}'' 不能出现在有默认值的形参之后",
        Category.PARSE,
    ),

    /** 默认值引用了后面的形参。 */
    E617_DEFAULT_REFERENCES_LATER_PARAM(
        "DSL_E617",
        "默认值不能引用后续形参 ''{0}''",
        Category.SEMANTIC,
    ),

    // ===== M5-c：类型系统基础 =====

    /** 表达式类型与目标类型不匹配。 */
    E620_TYPE_MISMATCH(
        "DSL_E620",
        "类型不匹配：期望 {0}，实际 {1}",
        Category.SEMANTIC,
    ),

    /** 引用了未知的类型名。 */
    E621_UNKNOWN_TYPE(
        "DSL_E621",
        "未知类型 ''{0}''",
        Category.SEMANTIC,
    ),

    /** 泛型实参个数与形参不一致。 */
    E622_GENERIC_ARG_COUNT_MISMATCH(
        "DSL_E622",
        "泛型 ''{0}'' 期望 {1} 个类型实参，实际 {2}",
        Category.SEMANTIC,
    ),

    /** 对可空表达式直接解引用。 */
    E623_NULLABLE_DEREFERENCE(
        "DSL_E623",
        "对可空类型 {0} 直接解引用，请使用 ''?.'' 或 ''!!''",
        Category.SEMANTIC,
    ),

    /** 将 null 赋给非空类型字段。 */
    E624_NULL_ASSIGN_TO_NON_NULLABLE(
        "DSL_E624",
        "不能将 null 赋给非空类型 {0} 的字段 ''{1}''",
        Category.SEMANTIC,
        fixHint = "若字段允许 null，请将类型改为 {0}?；否则请赋一个非 null 值",
    ),

    // ===== M5-e：suspend 强制显式（错误码预定义，校验待 .allin 资源迁移后启用） =====

    /** lambda 含 await 但未带 suspend 关键字。 */
    E625_SUSPEND_LAMBDA_MISSING_KEYWORD(
        "DSL_E625",
        "lambda 含 await 但未声明 suspend，请改写为 suspend '{ ... }'",
        Category.SEMANTIC,
        fixHint = "在 lambda 前加 suspend 关键字：suspend '{ ... }'",
    ),

    /** fun 含 await 但未声明 suspend。 */
    E626_SUSPEND_FUN_MISSING_KEYWORD(
        "DSL_E626",
        "fun ''{0}'' 含 await 但未声明 suspend",
        Category.SEMANTIC,
        fixHint = "在 fun 前加 suspend 关键字：suspend fun name(...)",
    ),

    /** 声明 suspend 但 body 无 await（仅 warning 级别）。 */
    E627_SUSPEND_DECLARED_NO_AWAIT(
        "DSL_E627",
        "''{0}'' 声明了 suspend 但 body 内无 await 调用",
        Category.SEMANTIC,
        fixHint = "若不需要异步，可移除 suspend 关键字",
    ),

    // ===== Stage 1：screen 生命周期 open / close 块 =====

    /** close 体内禁止使用 await。 */
    E640_CLOSE_FORBID_AWAIT(
        "DSL_E640",
        "close 块内禁止使用 await（close 体为同步，必须在销毁那一帧内跑完）",
        Category.SEMANTIC,
        fixHint = "把异步逻辑搬到 open 块或 fun 内执行，close 仅做同步清理",
    ),

    /** close 体内禁止调用 delay()。 */
    E641_CLOSE_FORBID_DELAY(
        "DSL_E641",
        "close 块内禁止调用 delay()（close 体为同步，必须在销毁那一帧内跑完）",
        Category.SEMANTIC,
        fixHint = "把延时逻辑搬到 open 块或 fun 内执行",
    ),

    /** close 体内禁止 net.request。 */
    E642_CLOSE_FORBID_NET_REQUEST(
        "DSL_E642",
        "close 块内禁止使用 net.request（close 体为同步，且不应触发服务端通信）",
        Category.SEMANTIC,
        fixHint = "把网络请求搬到 open 块或事件处理 fun 内执行",
    ),

    /** close 体内禁止 state 字段赋值。 */
    E643_CLOSE_FORBID_STATE_ASSIGN(
        "DSL_E643",
        "close 块内禁止给 state.{0} 赋值（state 由框架自动释放，无需手动清理）",
        Category.SEMANTIC,
        fixHint = "删除 state.{0} = ... 写入，state 由框架在屏幕销毁后自动回收",
    ),

    /** close 体内禁止 local.openScreen / local.back 等导航操作。 */
    E644_CLOSE_FORBID_LOCAL_NAV(
        "DSL_E644",
        "close 块内禁止调用 local.{0}（避免与销毁流程互锁）",
        Category.SEMANTIC,
        fixHint = "导航操作请放到事件处理 fun 中触发",
    ),

    /** close 体内禁止调用 suspend fun。 */
    E645_CLOSE_FORBID_SUSPEND_CALL(
        "DSL_E645",
        "close 块内禁止调用 suspend fun ''{0}''（close 体为同步，无法 await）",
        Category.SEMANTIC,
        fixHint = "请改为同步 fun，或把异步逻辑放到 open 块",
    ),

    /** open / close 块重复声明。 */
    E646_LIFECYCLE_DUPLICATE(
        "DSL_E646",
        "{0} 块只能声明一次",
        Category.PARSE,
        fixHint = "同一 screen 内 {0} 块只能保留一份，请删除多余的声明",
    ),

    /** open / close 块出现在非法位置（root 之后）。 */
    E647_LIFECYCLE_AFTER_ROOT(
        "DSL_E647",
        "{0} 块必须声明在 root 之前",
        Category.PARSE,
        fixHint = "把 {0} 块移动到 root 块之前（state / theme 之后）",
    ),

    // ===== Stage 11+：hud 顶层 anchor / layer / hide 声明 =====

    /** hud 顶层 anchor / layer / hide 重复声明。 */
    E648_HUD_PROP_DUPLICATE(
        "DSL_E648",
        "hud 顶层 ''{0}'' 只能声明一次",
        Category.PARSE,
        fixHint = "同一 hud 块内 ''{0}'' 只能保留一份，请删除多余的声明",
    ),

    /** hud 顶层 layer 取值非法（不是 above_chat / below_chat / below_vanilla / overlay）。 */
    E649_INVALID_HUD_LAYER(
        "DSL_E649",
        "hud layer 取值 ''{0}'' 非法，可选 above_chat / below_chat / below_vanilla / overlay",
        Category.PARSE,
        fixHint = "请使用 above_chat / below_chat / below_vanilla / overlay 之一",
    ),

    /** anchor / layer / hide 仅允许出现在 hud 块顶层；出现在 screen 块顶层时报错。 */
    E650_HUD_PROP_OUTSIDE_HUD(
        "DSL_E650",
        "''{0}'' 只能在 hud 块顶层声明，screen 块请放到 root 元素属性",
        Category.PARSE,
        fixHint = "把 ''{0}'' 声明移动到 hud 块顶层；或在 screen 中改为 root 元素属性",
    ),

    // ===== M6-a：overlay tooltip =====

    /** overlay 子树不可读写 screen.state。 */
    E730_OVERLAY_SUBTREE_FORBIDS_SCREEN_STATE(
        "DSL_E730",
        "overlay 子树不可读写 screen.state",
        Category.PARSE,
    ),

    /** overlay 子树禁止再嵌套 tooltip / modal / menu。 */
    E731_OVERLAY_SUBTREE_NESTED_OVERLAY(
        "DSL_E731",
        "overlay 子树嵌套 tooltip / modal / menu",
        Category.PARSE,
    ),

    /** overlay 子树根元素数量不为 1。 */
    E732_OVERLAY_SUBTREE_ROOT_COUNT_MISMATCH(
        "DSL_E732",
        "overlay 子树根元素数量不为 1",
        Category.PARSE,
    ),

    /** overlay 子树包含禁用元素（slot / canvas / inventory）。 */
    E733_OVERLAY_SUBTREE_FORBIDDEN_ELEMENT(
        "DSL_E733",
        "overlay 子树包含禁用元素（slot / canvas / inventory）",
        Category.PARSE,
    ),

    /** overlay 子树带事件 prop 但 overlay 未声明 interactive。 */
    E734_OVERLAY_SUBTREE_EVENT_PROP_REQUIRES_INTERACTIVE(
        "DSL_E734",
        "overlay 子树带事件 prop 但 overlay 未声明 interactive",
        Category.PARSE,
    ),

    // ===== M6-a / RFC Overlay：overlay tooltip 与 menu =====

    /** overlay 引用未注册组件名，或组件名非法。 */
    E735_OVERLAY_COMPONENT_UNREGISTERED(
        "DSL_E735",
        "overlay 引用未注册组件名或非法组件名 ''{0}''",
        Category.COMPONENT,
    ),

    /** overlay placement / animation 配置值非法。 */
    E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID(
        "DSL_E736",
        "overlay {0} 配置值 ''{1}'' 非法",
        Category.PARSE,
    ),

    /** menu item 缺少 text 或 onClick。 */
    E737_MENU_ITEM_REQUIRES_TEXT_OR_ON_CLICK(
        "DSL_E737",
        "menu item 缺少 text 或 onClick",
        Category.PARSE,
    ),

    /** 同一元素同时声明 rightClickMenu 与 contextMenu，但未显式指定不同触发。 */
    E738_MENU_TRIGGER_CONFLICT(
        "DSL_E738",
        "同一元素同时声明 rightClickMenu 与 contextMenu 时必须显式指定不同触发",
        Category.PARSE,
    ),

    // ===== M6-a：数据建模与字面量 =====

    /** Map 字面量必须用 `#{...}` 前缀。 */
    E747_MAP_LITERAL_REQUIRES_HASH_PREFIX(
        "DSL_E747",
        "Map 字面量必须用 #'{'...'}'  前缀（旧 '{ k = v }' 形态已废弃）",
        Category.PARSE,
        fixHint = "请改用 Map 字面量 #'{'\"k\": v'}'",
    ),

    /** 引用了未声明的 data 类型 / 同名 data 重复声明。多种文案，调用点自填完整消息。 */
    E748_UNDECLARED_DATA_TYPE(
        "DSL_E748",
        "{0}",
        Category.SEMANTIC,
    ),

    /** data 必填字段未提供。 */
    E749_DATA_REQUIRED_FIELD_MISSING(
        "DSL_E749",
        "data ''{0}'' 缺少必填字段 ''{1}''",
        Category.SEMANTIC,
        fixHint = "在 Map 字面量中补齐 \"{1}\": <值>，或在 data 声明中为该字段添加默认值",
    ),

    /** data 字段类型不匹配。 */
    E751_DATA_FIELD_TYPE_MISMATCH(
        "DSL_E751",
        "data ''{0}'' 字段 ''{1}'' 期望类型 {2}，实际为 {3}",
        Category.SEMANTIC,
    ),

    /** data 字段名错误（引用了 data 未声明的字段）。 */
    E756_DATA_UNKNOWN_FIELD(
        "DSL_E756",
        "data ''{0}'' 不存在字段 ''{1}''",
        Category.SEMANTIC,
        fixHint = "请确认字段名拼写，或在 data \"{0}\" 中添加该字段",
    ),

    /** data 同名字段重复声明。 */
    E757_DATA_DUPLICATE_FIELD(
        "DSL_E757",
        "data ''{0}'' 字段 ''{1}'' 重复声明",
        Category.PARSE,
    ),

    /** Map 字面量键非字符串字面量。 */
    E758_MAP_KEY_NOT_STRING_LITERAL(
        "DSL_E758",
        "Map 字面量键必须是字符串字面量",
        Category.PARSE,
        fixHint = "请把键写成双引号字符串，例如 \"name\": value",
    ),

    /** data 字段命名不合法。 */
    E759_DATA_FIELD_NAME_INVALID(
        "DSL_E759",
        "data 字段名 ''{0}'' 不合法，必须形如 [a-z][A-Za-z0-9_]*",
        Category.PARSE,
    ),

    /** data 类型同名重复声明。 */
    E765_DUPLICATE_DATA_DECL(
        "DSL_E765",
        "data ''{0}'' 重复声明",
        Category.SEMANTIC,
    ),

    /** Map 字面量含 data 类型未声明的字段（疑似拼写错误，仅 warning）。 */
    W730_MAP_LITERAL_UNKNOWN_DATA_FIELD(
        "DSL_W730",
        "Map 字面量含 data ''{0}'' 未声明的字段 ''{1}''（疑似拼写错误）",
        Category.SEMANTIC,
        fixHint = "请确认字段名拼写，或在 data \"{0}\" 中添加该字段",
    ),

    // ===== M6-b：容器形参 + source 属性 =====

    /** 容器无 source 但声明了形参。 */
    E700_CONTAINER_PARAMS_WITHOUT_SOURCE(
        "DSL_E700",
        "容器 ''{0}'' 声明了形参但缺少 source 属性",
        Category.SEMANTIC,
        fixHint = "删除形参列表，或为容器添加 source = expr",
    ),

    /** 容器有 source 但未声明形参。 */
    E701_CONTAINER_SOURCE_WITHOUT_PARAMS(
        "DSL_E701",
        "容器 ''{0}'' 声明了 source 但缺少形参",
        Category.SEMANTIC,
        fixHint = "声明形参，例如 column \"x\" '{' item -> source = ... '}'",
    ),

    /** source 表达式静态类型不是 List 或 Map。 */
    E702_CONTAINER_SOURCE_NOT_ITERABLE(
        "DSL_E702",
        "容器 ''{0}'' 的 source 表达式类型为 {1}，必须求值为 List 或 Map",
        Category.SEMANTIC,
        fixHint = "source 必须是 List 或 Map，请检查表达式类型",
    ),

    /** 形参数与 source 类型不匹配。 */
    E703_CONTAINER_PARAM_COUNT_MISMATCH(
        "DSL_E703",
        "容器 ''{0}'' 形参数 {1} 与 source 类型不匹配（{2}）",
        Category.SEMANTIC,
        fixHint = "List 用单参 '{' item -> '}'，Map 用双参 '{' key, value -> '}'",
    ),

    /** 形参类型与 source 元素类型不匹配。 */
    E704_CONTAINER_PARAM_TYPE_MISMATCH(
        "DSL_E704",
        "容器 ''{0}'' 形参 ''{1}'' 类型 {2} 与 source 元素类型 {3} 不匹配",
        Category.SEMANTIC,
        fixHint = "修正形参类型标注，使其与 source 元素类型一致",
    ),

    /** source 元素类型为 data 但缺 id 字段（默认 keyExpr 无效）。 */
    E705_CONTAINER_DATA_MISSING_ID(
        "DSL_E705",
        "容器 ''{0}'' 的 source 元素 data ''{1}'' 缺少 id 字段，无法生成默认 key",
        Category.SEMANTIC,
        fixHint = "在 data \"{1}\" 中添加 id: String 字段，或写显式 key = expr",
    ),

    /** source 是 List<Map>/List<Any>，未显式声明 key。 */
    E706_CONTAINER_KEY_REQUIRED(
        "DSL_E706",
        "容器 ''{0}'' 的 source 元素类型为 {1}，必须显式声明 key = expr",
        Category.SEMANTIC,
        fixHint = "为容器加 key = <表达式>，用于标识每个子项",
    ),

    /** 嵌套同名形参（外层形参被遮蔽）。 */
    W707_CONTAINER_PARAM_SHADOWING(
        "DSL_W707",
        "容器 ''{0}'' 内层形参 ''{1}'' 遮蔽了外层同名形参",
        Category.SEMANTIC,
        fixHint = "重命名内层形参以避免遮蔽外层形参",
    ),

    /** 容器自身属性引用了本容器 source 形参（应放到子节点中）。 */
    E708_CONTAINER_SELF_PROP_REFS_SOURCE_PARAM(
        "DSL_E708",
        "容器 ''{0}'' 自身属性 ''{1}'' 引用了 source 形参 ''{2}''，容器自身属性在每个 item 上必须保持一致",
        Category.SEMANTIC,
        fixHint = "若需要按 item 不同求值，请把属性放到子节点里；容器自身属性在每个 item 上保持一致",
    ),

    // ===== M6-d：内联 component 声明 =====

    /** 内联 component 名与已注册的跨文件 component 同名。 */
    E710_INLINE_COMPONENT_NAME_CONFLICT_CROSS_FILE(
        "DSL_E710",
        "内联 component ''{0}'' 与已存在的跨文件 component 同名",
        Category.COMPONENT,
        fixHint = "重命名内联 component，或删除同名跨文件 component",
    ),

    /** 内联 component 必须包含 props 块和 root 块。 */
    E711_INLINE_COMPONENT_MISSING_PROPS_OR_ROOT(
        "DSL_E711",
        "内联 component ''{0}'' 必须同时声明 props 块和 root 块",
        Category.PARSE,
    ),

    /** 同一 screen 内两个内联 component 同名。 */
    E712_INLINE_COMPONENT_DUPLICATE_NAME(
        "DSL_E712",
        "内联 component 名 ''{0}'' 在同一 screen 内重复声明",
        Category.COMPONENT,
    ),

    // ===== Stage4：PAPI 编译期占位符 =====

    /** 同一 screen 内重复声明 papi 块。 */
    E713_PAPI_DUPLICATE_BLOCK(
        "DSL_E713",
        "papi 块只能声明一次",
        Category.SEMANTIC,
    ),

    /** 同一 papi 块内 short_name 重复。 */
    E714_PAPI_DUPLICATE_SHORT_NAME(
        "DSL_E714",
        "papi short_name ''{0}'' 重复声明",
        Category.SEMANTIC,
    ),

    /** refreshMs 必须为正且不得低于最小刷新间隔。 */
    E715_PAPI_REFRESH_MS_INVALID(
        "DSL_E715",
        "papi ''{0}'' refreshMs={1} 非法，必须大于 0 且不低于 {2}",
        Category.SEMANTIC,
    ),

    /** PlaceholderRegistry 未允许或显式拒绝该表达式。 */
    E716_PAPI_REGISTRY_REJECTED(
        "DSL_E716",
        "papi ''{0}'' 对应表达式 ''{1}'' 未被 PlaceholderRegistry 允许",
        Category.SEMANTIC,
    ),

    /** pipe 内禁止 await、网络请求、导航与 state 写入等副作用。 */
    E717_PAPI_PIPE_SIDE_EFFECT(
        "DSL_E717",
        "papi ''{0}'' pipe 含不允许的副作用: {1}",
        Category.SEMANTIC,
    ),

    /** papi 字段仅支持指定形态，例如 bind=state.xxx / onChange=::fun。 */
    E718_PAPI_FIELD_UNSUPPORTED(
        "DSL_E718",
        "papi ''{0}'' 字段 ''{1}'' 仅支持 {2}",
        Category.SEMANTIC,
    ),

    /** short_name 必须是合法 DSL 标识符。 */
    E719_PAPI_SHORT_NAME_INVALID(
        "DSL_E719",
        "papi short_name ''{0}'' 不合法，必须是 DSL 标识符",
        Category.SEMANTIC,
    ),

    // ===== M6-e：::fun(args) 引用扩展 =====

    /** `::fun(args)` 中 fun 不存在（对应 RFC §7.5 E720）。 */
    E739_FUN_REF_BOUND_ARG_REF_NOT_FOUND(
        "DSL_E739",
        "::fun(args) 引用的 fun ''{0}'' 不存在",
        Category.SEMANTIC,
    ),

    /** toast 同时声明 message 与 content。 */
    E740_TOAST_MESSAGE_CONTENT_MUTEX(
        "DSL_E740",
        "toast 同时声明 message 属性与 content 内容块",
        Category.SEMANTIC,
    ),

    /** toast content 子树违反约束。 */
    E741_TOAST_CONTENT_CONSTRAINT_VIOLATION(
        "DSL_E741",
        "toast content 子树违反约束：{0}",
        Category.SEMANTIC,
    ),

    /** toast component 引用非法或未注册。 */
    E742_TOAST_COMPONENT_INVALID(
        "DSL_E742",
        "toast component 引用非法或未注册：{0}",
        Category.SEMANTIC,
    ),

    /** `::fun(args)` 实参数超过 fun 形参数（原 DSL_E740，迁移避免与 Toast 诊断冲突）。 */
    E766_FUN_REF_BOUND_ARG_COUNT_EXCEEDED(
        "DSL_E766",
        "::fun(args) 实参数 {0} 超过 fun ''{1}'' 形参数 {2}",
        Category.SEMANTIC,
    ),

    /** `::fun(args)` 命名实参 name 不在形参列表（原 DSL_E741，迁移避免与 Toast 诊断冲突）。 */
    E767_FUN_REF_BOUND_NAMED_ARG_UNKNOWN(
        "DSL_E767",
        "::fun(args) 命名实参 ''{0}'' 不在 fun ''{1}'' 的形参列表中",
        Category.SEMANTIC,
    ),

    // ===== M6-f：商店必备能力补齐 =====

    /** enabled 属性字面量必须为 Boolean（原 DSL_E742，迁移避免与 Toast 诊断冲突）。 */
    E768_ENABLED_TYPE_MISMATCH(
        "DSL_E768",
        "属性 ''enabled'' 期望 Boolean，实际为 {0}",
        Category.SEMANTIC,
    ),

    /** visible 属性字面量必须为 Boolean。 */
    E743_VISIBLE_TYPE_MISMATCH(
        "DSL_E743",
        "属性 ''visible'' 期望 Boolean，实际为 {0}",
        Category.SEMANTIC,
    ),

    /** padding 单边属性（paddingTop/Right/Bottom/Left）字面量必须为 Number。 */
    E744_PADDING_SIDE_TYPE_MISMATCH(
        "DSL_E744",
        "属性 ''{0}'' 期望 Number，实际为 {1}",
        Category.SEMANTIC,
    ),

    /** format 第一参数字面量必须为字符串。 */
    E745_FORMAT_FIRST_ARG_NOT_STRING(
        "DSL_E745",
        "format(...) 第一参数必须为字符串，实际为 {0}",
        Category.SEMANTIC,
    ),

    /** toFixed 参数字面量必须为 Number。 */
    E746_TO_FIXED_ARG_NOT_NUMBER(
        "DSL_E746",
        "Number.toFixed(...) 参数必须为 Number，实际为 {0}",
        Category.SEMANTIC,
    ),

    // ===== M6-h：弹层系统 =====

    /** modal / toast 必须显式声明 visible 属性。 */
    E750_MODAL_DIALOG_TOAST_VISIBLE_REQUIRED(
        "DSL_E750",
        "{0} ''{1}'' 必须显式声明 visible 属性",
        Category.SEMANTIC,
        fixHint = "在元素体内补一行 visible = state.xxx",
    ),

    /** toast kind 字面量取值未知（不在 info / success / warning / error）。 */
    E752_TOAST_KIND_UNKNOWN(
        "DSL_E752",
        "toast kind 取值 ''{0}'' 未知，可选 info / success / warning / error",
        Category.SEMANTIC,
        fixHint = "请使用 info / success / warning / error 之一",
    ),

    /** toast position 字面量取值未知（不在 top / bottom / center）。 */
    E753_TOAST_POSITION_UNKNOWN(
        "DSL_E753",
        "toast position 取值 ''{0}'' 未知，可选 top / bottom / center",
        Category.SEMANTIC,
        fixHint = "请使用 top / bottom / center 之一",
    ),

    /** toast duration 字面量必须为非负 Number。 */
    E754_TOAST_DURATION_INVALID(
        "DSL_E754",
        "toast duration 必须为非负 Number，实际为 {0}",
        Category.SEMANTIC,
        fixHint = "duration 单位为毫秒，0 表示不自动消失",
    ),

    /**
     * M6 review B：data 类型 prop 暂不支持默认值。
     *
     * `props { item: Item = ... }` 这类写法被禁止，
     * 因为 data 字面量结构复杂、嵌套语义跨过组件边界，第一版仅支持必传。
     */
    E755_DATA_PROP_DEFAULT_NOT_ALLOWED(
        "DSL_E755",
        "data 类型 prop ''{0}'' 暂不支持默认值",
        Category.PARSE,
        fixHint = "请将 prop ''{0}'' 改为必传，由调用方提供 data 字面量",
    ),

    // ===== M6-i：动画反应式回调 =====

    /**
     * `on { trigger = "anim" -> <funRef> }` 中 onComplete 回调必须是 `::fun` 引用
     * （允许 `::fun(args)` 部分应用）；不接受 lambda、字面量或任意表达式。
     */
    E760_TRIGGER_ON_COMPLETE_MUST_BE_FUN_REF(
        "DSL_E760",
        "on 触发器 ''{0}'' 的 onComplete 回调必须是 ''::fun'' 引用，实际为 {1}",
        Category.SEMANTIC,
        fixHint = "请使用 ::funName 或 ::funName(args) 形式提供回调",
    ),

    /** `animate(name, ...)` 中 name 字面量未在 screen 的 animation 声明中找到。 */
    E761_ANIMATE_NAME_NOT_FOUND(
        "DSL_E761",
        "animate(...) 引用的 animation ''{0}'' 不存在",
        Category.SEMANTIC,
    ),

    // E762（animate target 元素 id 不存在）/ E763（effects 表达式类型不匹配）
    // 当前阶段保留在 runtime：编译期通常无法拿到完整的元素 id 全集 / 表达式类型，
    // 这两类错误在 IrInterpreter / PropAppliers 中以 DslRuntimeError 抛出。

    // ====================================================================
    // RFC-RECOMPOSITION (E840-E844)：元素树位置 if/when 条件分支重组相关错误
    // ====================================================================

    /** inventory / slotGrid 不能放在 conditional 分支内（SlotProvider 注册路径未做 mount/unmount）。 */
    E840_INVENTORY_IN_CONDITIONAL(
        "DSL_E840",
        "inventory / slotGrid 不能放在 conditional 分支内（kind={0})",
        Category.SEMANTIC,
        fixHint = "请把 {0} 节点放到 conditional 之外，或用其他方式控制其可见性（如 visible 属性）",
    ),

    /** conditional branch body 必须是单根 element，多根或空体直接报错。 */
    E841_CONDITIONAL_BRANCH_MULTI_ROOT(
        "DSL_E841",
        "conditional 分支体必须是单根 element（不允许多根或空体）",
        Category.PARSE,
        fixHint = "把分支体改为一个根元素：`branch -> column \"x\" {{ ... }}` 或 `branch -> {{ column \"x\" {{ ... }} }}`",
    ),

    /** conditional 嵌套深度超过限制（默认 8）。 */
    E842_CONDITIONAL_NESTING_TOO_DEEP(
        "DSL_E842",
        "conditional 嵌套深度超过 {0}",
        Category.SEMANTIC,
        fixHint = "建议把内层条件提到外层 state，或拆成多个 conditional 容器",
    ),

    /** when 分支值必须是字面量（与 ActionBlockParser.parseWhenValue 同约束）。 */
    E843_CONDITIONAL_BRANCH_VALUE_NOT_LITERAL(
        "DSL_E843",
        "when 分支值必须是字面量（不允许变量 / 表达式）",
        Category.PARSE,
    ),

    /** virtualList 内的 conditional 与窗口化复用语义冲突，编译期拒绝。 */
    E844_CONDITIONAL_INSIDE_VIRTUAL_LIST(
        "DSL_E844",
        "conditional 不能直接放在 virtualList 内",
        Category.SEMANTIC,
        fixHint = "请把 conditional 放到 virtualList 模板内部的子元素，而非模板根",
    ),

    // ===== $for：元素树位置 for 循环重组 =====

    /** for 循环体必须是单根 element，多根或空体直接报错（与 conditional 的 E841 对齐）。 */
    E845_FOR_BODY_MULTI_ROOT(
        "DSL_E845",
        "for 循环体必须是单根 element（不允许多根或空体）",
        Category.PARSE,
        fixHint = "请把多个元素包进一个容器（如 column / box），保证 for 体只有一个根元素",
    ),

    /** for 嵌套深度超过限制（与 conditional 共享深度预算，默认 8）。 */
    E846_FOR_NESTING_TOO_DEEP(
        "DSL_E846",
        "for / conditional 嵌套深度超过 {0}",
        Category.SEMANTIC,
        fixHint = "请减少元素树位置 for / if / when 的嵌套层级",
    ),

    /** virtualList 内的 for 与窗口化复用语义冲突，编译期拒绝。 */
    E847_FOR_INSIDE_VIRTUAL_LIST(
        "DSL_E847",
        "for 不能直接放在 virtualList 内",
        Category.SEMANTIC,
        fixHint = "请把 for 放到 virtualList 模板内部的子元素，而非模板根",
    ),

    /** inventory / slotGrid 不能放在 for 循环体内（SlotProvider 注册路径未做 mount/unmount）。 */
    E848_INVENTORY_IN_FOR(
        "DSL_E848",
        "inventory / slotGrid 不能放在 for 循环体内（kind={0})",
        Category.SEMANTIC,
        fixHint = "请把 inventory / slotGrid 移到 for 之外的静态位置",
    ),

    /** Map 解构形态的 for 不支持显式 key 子句，两者语义冲突。 */
    E849_FOR_MAP_EXPLICIT_KEY(
        "DSL_E849",
        "Map 解构形态的 for 不支持显式 key 子句（; key = ...）",
        Category.SEMANTIC,
        fixHint = "Map 解构以 entry.key 作为 diff key；请移除 '; key = ...' 子句，或改用 List 形态（for (item in map.values; key = ...)）",
    ),

    // ===== Stage B：subscribe 订阅系统 =====

    /** 同一 screen 内重复声明 subscribe 顶层块。 */
    E780_SUBSCRIBE_BLOCK_DUPLICATE(
        "DSL_E780",
        "subscribe 顶层块重复声明",
        Category.SEMANTIC,
        fixHint = "请将所有订阅合并到一个 subscribe 块中",
    ),

    /** subscribe 命名空间名非法（应符合标识符规则）。 */
    E781_SUBSCRIBE_NAMESPACE_INVALID(
        "DSL_E781",
        "subscribe 命名空间名 ''{0}'' 非法（应符合标识符规则）",
        Category.SEMANTIC,
        fixHint = "命名空间名必须形如 [A-Za-z_][A-Za-z0-9_]*",
    ),

    /** subscribe 主题名非法（应符合标识符规则）。 */
    E782_SUBSCRIBE_TOPIC_INVALID(
        "DSL_E782",
        "subscribe 主题名 ''{0}'' 非法（应符合标识符规则）",
        Category.SEMANTIC,
        fixHint = "主题名必须形如 [A-Za-z_][A-Za-z0-9_]*",
    ),

    /** subscribe (namespace, topic) 重复声明。 */
    E783_SUBSCRIBE_TOPIC_DUPLICATE(
        "DSL_E783",
        "subscribe ({0}, {1}) 重复声明",
        Category.SEMANTIC,
        fixHint = "同一 (namespace, topic) 只能声明一次",
    ),

    /** subscribe handler 必须是 ::funRef 或 1 参 lambda。 */
    E784_SUBSCRIBE_HANDLER_INVALID(
        "DSL_E784",
        "subscribe handler 必须是 ::funRef 或 1 参 lambda{0}",
        Category.SEMANTIC,
        fixHint = "请使用 ::funName 或 { event -> ... } 形式提供 handler",
    ),

    /** subscribe 条目未知字段（仅支持 handler）。 */
    E785_SUBSCRIBE_FIELD_UNKNOWN(
        "DSL_E785",
        "subscribe 条目未知字段 ''{0}''（仅支持 handler）",
        Category.SEMANTIC,
        fixHint = "subscribe 条目仅支持 handler 字段",
    ),

    // ===== ADR-0036：tooltip 专用数据请求 =====

    /** tooltip 块内 `data NAME = net.tooltip(...)` 句柄名重复声明。 */
    E786_TOOLTIP_DATA_DUPLICATE(
        "DSL_E786",
        "tooltip data 句柄名 ''{0}'' 重复声明",
        Category.PARSE,
        fixHint = "同一 tooltip 块内每个 data 句柄名只能声明一次，请改用不同名字",
    ),

    /** tooltip `data NAME = net.tooltip("ns.handler", ...)` 的 channel 字面量缺少 '.' 分隔。 */
    E787_TOOLTIP_DATA_CHANNEL_INVALID(
        "DSL_E787",
        "tooltip data 的 channel ''{0}'' 非法，必须为 \"namespace.handler\" 形式",
        Category.PARSE,
        fixHint = "请使用形如 \"item.setinfo\" 的 channel 字面量（含 '.' 分隔命名空间与处理器）",
    ),

    /** ADR-0036：overlay（tooltip 等）子树内引用了带内部 state 的组件——state 在 overlay 不刷新，禁止。 */
    E788_STATEFUL_COMPONENT_IN_OVERLAY(
        "DSL_E788",
        "overlay 子树内不可使用带 state 的组件 ''{0}''（请改用纯 props 组件）",
        Category.COMPONENT,
        fixHint = "tooltip 等 overlay 内复用的组件须为纯 props（无 state）；把数据通过 props 传入，状态交给外层 tooltip data 句柄",
    ),

    // ===== Stage 3：class 内 fun 作用域守卫 =====

    /**
     * Stage 3：class 内 fun 违反作用域约束。
     *
     * class 内 fun（Stage 6 起支持）禁止访问外层 screen.state、screen 级 fun、
     * 顶层 var、source 形参等"宿主作用域"对象，只能引用本 class 字段、本 class
     * 其它 fun、内置函数与字面量。本错误码由 [ClassBodyChecker] 上报。
     *
     * Stage 3 仅定义错误码与 checker 骨架，因 Stage 2 解析层 ClassDecl.funs
     * 一律为空，当前不会被触发。Stage 6 接入语法后立即生效。
     */
    E790_CLASS_BODY_FUN_SCOPE_VIOLATION(
        "DSL_E790",
        "class ''{0}'' 内 fun ''{1}'' 不能访问宿主作用域对象 ''{2}''",
        Category.SEMANTIC,
        fixHint = "class 内 fun 仅可引用本 class 字段、本 class 其它 fun 与内置函数；如需访问 screen 状态请改写为顶层 fun",
    ),

    /**
     * Stage 6：class 内 fun 与字段同名 / fun 同名重复声明。
     *
     * data class 内的字段名与 fun 名共享一个命名空间；同名会造成成员访问歧义。
     * 由 ClassBodyChecker 在收集到 funs 后做去重校验。
     */
    E791_CLASS_MEMBER_DUPLICATE(
        "DSL_E791",
        "class ''{0}'' 内成员 ''{1}'' 重复声明",
        Category.SEMANTIC,
        fixHint = "data 内的字段名与 fun 名不可重名；请重命名其中之一",
    ),

    /**
     * `data "Name" { ... }` / `object "Name" { ... }` 的字符串类型名形式已废弃。
     *
     * 类型名应当使用裸标识符形式：`data Item { ... }` / `object Util { ... }`，
     * 与使用侧（`var x: Item` / `props { item: Item }`）保持对称。字符串形式
     * 兼容期内仍可解析，但会在解析阶段发出该 warning，提示开发者迁移。
     */
    E792_CLASS_NAME_STRING_DEPRECATED(
        "DSL_E792",
        "{0} 类型名使用字符串形式 \"{1}\" 已废弃，请改用裸标识符 {1}",
        Category.PARSE,
        fixHint = "把 `{0} \"{1}\" '{' ... '}'` 改写为 `{0} {1} '{' ... '}'`，与使用侧 `: {1}` 保持一致",
    ),

    /**
     * `data` / `object` 类型名标识符形式不合法（必须以大写字母起始）。
     *
     * 与字段名规则 [E759_DATA_FIELD_NAME_INVALID] 对称：字段必须小写起始，
     * 类型必须大写起始，使用侧 `: TypeName` 才能与首字母大写 / 小写规则区分。
     */
    E793_CLASS_NAME_INVALID(
        "DSL_E793",
        "{0} 类型名 \"{1}\" 不合法，必须以大写字母起始（驼峰命名，例如 Item / UserOrder）",
        Category.PARSE,
        fixHint = "把类型名首字母改为大写：`{0} {1}` → `{0} <首字母大写>`",
    ),

    // ===== M9 Stage 6（INFO 级别）=====
    //
    // 11 个新诊断码：plan §9.2 原指定 E625-E635 与 M5-e suspend 关键字诊断既有冲突，
    // 改用 E660-E670 实现。所有新诊断初始注册为 INFO 严重度——不出红 / 黄波浪线，
    // 仅在 inspection 列表与 IDE settings 内可见，避免对 m6-test-project 175 个
    // 历史样本造成误报洪水。
    //
    // **分期触发策略**（与 plan §9.3 阶段 6b 的务实裁剪）：
    // - 阶段 6b 第一批触发（4 个）：E660 / E662 / E666 / E670——覆盖最高频的 4 类
    //   类型不一致场景，依赖现有 [TypeConstraintChecker] 扫描机制即可上线。
    // - **M9.5 Stage 4 第二批触发（4 个）**：E661 / E665 / E667 / E668——分别覆盖 state
    //   字段二次赋值类型不匹配、when 分支返回类型不一致、Elvis 右侧类型不容、is 运算符
    //   目标类型未识别四类语义提醒，由 [TypeConstraintChecker] 在原 walk 基础上扩展接入。
    // - **M10 Stage 4 全部触发（3 个）**：E663 / E664 / E669——依托 [LambdaParamChecker]
    //   与 [NullSafetyChecker] 在拆分后的聚合器内分别接入。E663 检查 lambda 形参未标注
    //   且 prop 不在 [top.wcpe.allincore.dsl.compiler.api.EventTypeMetadata.resolveLambdaParamTypes]
    //   表中；E664 检查 net.request callback 形参标注非 Any / 非 Nullable；E669 检查
    //   data 字段访问名既不在同文件 data 类也不在跨文件 [top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable]
    //   的 dataFields 中时报 INFO。

    /**
     * fun 体内对形参二次赋值（违反"形参 immutable"约定）。
     *
     * 阶段 6b 启用：[top.wcpe.allincore.dsl.compiler.semantic.TypeConstraintChecker] 在
     * 走 fun body 时维护"当前 fun 形参名集合"，命中 LocalLValue 时触发 INFO 级提醒。
     */
    E660_PARAM_REASSIGN(
        "DSL_E660",
        "fun 形参 ''{0}'' 在体内被二次赋值（建议视为 immutable）",
        Category.SEMANTIC,
        fixHint = "若需要可变副本，请改用 var 在 fun 体内重新声明：var x = {0}",
    ),

    /**
     * state 字段二次赋值类型不匹配。
     *
     * **M9.5 Stage 4 启用**：[top.wcpe.allincore.dsl.compiler.semantic.TypeConstraintChecker]
     * 在 AssignStmt 阶段把 lhs 是 [top.wcpe.allincore.dsl.compiler.ast.LValueAst.StateLValue] 的
     * 赋值与已解析的 stateFieldIrTypes 比对；rhs 推断类型双向不容时报 INFO。null 字面量赋值
     * 仍走 E624 错误级路径，本码不重复。
     */
    E661_STATE_FIELD_REASSIGN_TYPE_MISMATCH(
        "DSL_E661",
        "state 字段 ''{0}'' 类型 {1}，赋值为不兼容的 {2}",
        Category.SEMANTIC,
    ),

    /**
     * 跨语句 var 类型不一致（即便无标注也提醒）。
     *
     * 阶段 6b 启用：[TypeConstraintChecker] 维护未标注 var 的"初值推断类型表"，二次
     * 赋值类型不容时报 INFO。注意与 E620 的边界：
     * - **已标注**类型不一致 → E620（错误级，原行为）
     * - **未标注**前后类型 drift → E662（INFO 级，新增）
     */
    E662_LOCAL_VAR_TYPE_DRIFT(
        "DSL_E662",
        "var ''{0}'' 初值类型 {1} 与后续赋值类型 {2} 不兼容",
        Category.SEMANTIC,
        fixHint = "若期望多态，请显式标注 var ''{0}'': Any 或更宽泛的类型",
    ),

    /**
     * lambda 形参类型推断失败：上下文不足以确定。
     *
     * **M10 Stage 4 启用**：[top.wcpe.allincore.dsl.compiler.semantic.LambdaParamChecker]
     * 检查 lambda 形参未标注且调用点 prop 不在
     * [top.wcpe.allincore.dsl.compiler.api.EventTypeMetadata.resolveLambdaParamTypes] 表中
     * 时报 INFO。命中表的 prop（如 onClick / onChange）由表回填形参类型，不触发本码。
     */
    E663_LAMBDA_PARAM_TYPE_INFERENCE_FAILED(
        "DSL_E663",
        "lambda 形参 ''{0}'' 类型推断失败（建议显式标注：{0}: Type）",
        Category.SEMANTIC,
    ),

    /**
     * net.request callback 形参的标注类型与推断不容。
     *
     * **M10 Stage 4 启用**：[top.wcpe.allincore.dsl.compiler.semantic.LambdaParamChecker]
     * 检查 [top.wcpe.allincore.dsl.compiler.ast.ExprAst.NetRequestCall.callback] 的首形参
     * 显式标注非 Any 且非 Nullable 时报 INFO——payload 实际类型是 Any?，标注成具体非空
     * 类型必然不容；标注 `Any?` / `Number?` 等任意 Nullable 包装即放行。FunRefExpr 形态
     * 不在范围。
     *
     * 放行规则：标注为 `Any`（DSL 动态类型，[top.wcpe.allincore.dsl.compiler.type.IrType.AnyT]
     * 语义是"所有类型互相相容"，能正确接收可空 payload）或任意 `Nullable(_)`（如
     * `Number?` / `Any?`）时不触发——这两类都能正确接收可空 payload。仅在标注为非空具体
     * 类型（如 `Number` / `String` / `Boolean`）时才报 INFO。
     */
    E664_NET_REQUEST_CALLBACK_TYPE_MISMATCH(
        "DSL_E664",
        "net.request callback 形参 ''{0}'' 标注类型 {1} 与推断 {2} 不兼容",
        Category.SEMANTIC,
    ),

    /**
     * when 分支返回类型不一致。
     *
     * **M9.5 Stage 4 启用**：[TypeConstraintChecker] 在 walkExpr(WhenExpr) 内推断每个分支
     * 末端表达式类型并两两比较；任意一对双向 isAssignableTo 都失败时报 INFO（仅报第一对，
     * 避免噪声）。AnyT 分支整体跳过，避免对动态值假阳性。
     */
    E665_WHEN_BRANCH_TYPE_INCONSISTENT(
        "DSL_E665",
        "when 表达式分支返回类型不一致（{0} vs {1}）",
        Category.SEMANTIC,
        fixHint = "请让所有分支返回兼容类型，或用 Any 显式标注变量类型",
    ),

    /**
     * 可空类型直接解引用（流敏感简化版）。
     *
     * 阶段 6b 启用：[TypeConstraintChecker] 处理 [top.wcpe.allincore.dsl.compiler.ast.ExprAst.MemberAccess]
     * 时，若 target 推断类型为 Nullable（且 inner != AnyT），报 INFO。
     * 简化策略——名义上是流敏感，实现上"看到非 ?. 访问可空类型立即报"，可能误报，
     * 但已经是 INFO 级别，可接受。
     */
    E666_NULLABLE_DEREFERENCE_FLOW_INSENSITIVE(
        "DSL_E666",
        "可空类型 {0} 的字段 / 方法访问 ''{1}'' 未做空安全判断",
        Category.SEMANTIC,
        fixHint = "改用 ''?.{1}'' 或先 ''{0} ?: defaultValue'' 兜底",
    ),

    /**
     * a ?: 0 但 a 是 String：右侧类型与左侧 inner 不容。
     *
     * **M9.5 Stage 4 启用**：[TypeConstraintChecker] 在 walkExpr(Elvis) 内推断左右两侧类型，
     * 剥掉左侧 Nullable 包装后与右侧做双向兼容性比较；不容时报 INFO。null 字面量在右侧
     * （Nullable(AnyT)）一律放行；AnyT 一侧也放行，避免对动态值假阳性。
     */
    E667_ELVIS_RIGHT_TYPE_INCOMPATIBLE(
        "DSL_E667",
        "Elvis 右侧类型 {0} 与左侧 ''{1}'' 不兼容",
        Category.SEMANTIC,
    ),

    /**
     * is 运算符目标类型未识别。
     *
     * **M9.5 Stage 4 启用**：[TypeConstraintChecker] 在 walkExpr(IsExpr) 内调
     * [top.wcpe.allincore.dsl.compiler.type.TypeResolver.resolve] 反查命名类型；data 声明
     * 已注册的类型与裸名容器（List / Map）单独识别放行，其它未识别名报 INFO。运行时
     * `is` 的支持范围以 ExprEvaluator.checkIsType 为准（Number/Int/Long/Double/String/
     * Boolean/Bool/Map/List/Any/Unit）。
     */
    E668_IS_TARGET_TYPE_UNKNOWN(
        "DSL_E668",
        "''is'' 运算符目标类型 ''{0}'' 未识别（仅支持已声明的 data 类型与内置基本类型）",
        Category.SEMANTIC,
    ),

    /**
     * data 字段访问类型推断失败。
     *
     * **M10 Stage 4 启用**：[top.wcpe.allincore.dsl.compiler.semantic.NullSafetyChecker]
     * 在 MemberAccess 上推断 target 类型为 [top.wcpe.allincore.dsl.compiler.type.IrType.DataInstance]
     * 时，按字段名查同文件 [top.wcpe.allincore.dsl.compiler.semantic.VarTypeEnv.localDataFields]
     * 与跨文件 [top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable.dataFields]——两表均
     * 未命中时报 INFO。占位符：{0}=data 类名 / {1}=访问的字段名。
     */
    E669_DATA_FIELD_TYPE_INFERENCE_FAILED(
        "DSL_E669",
        "data 字段 ''{0}.{1}'' 类型推断失败（请检查 data 声明）",
        Category.SEMANTIC,
    ),

    /**
     * for 循环迭代变量二次赋值。
     *
     * 阶段 6b 启用：[TypeConstraintChecker] 处理 ForInStmt 时维护"当前 for 迭代变量名"
     * 集合，命中 LocalLValue 时报 INFO；进出 body 用 try / finally 恢复以保证嵌套循环
     * 的迭代变量名不互相污染。
     */
    E670_FOR_LOOP_VAR_REASSIGN(
        "DSL_E670",
        "for 循环迭代变量 ''{0}'' 不能在循环体内被赋值",
        Category.SEMANTIC,
        fixHint = "若需要可变副本，请在循环体首声明：var copy = {0}",
    ),

    /** theme extends 继承链存在循环（extends 形成环）。 */
    E794_THEME_EXTENDS_CYCLE(
        "DSL_E794",
        "theme ''{0}'' 的继承链存在循环（extends 形成环）",
        Category.SEMANTIC,
        fixHint = "检查 extends 引用，移除循环依赖",
    ),

    /** theme extends 引用的父主题未注册。 */
    E795_THEME_EXTENDS_UNRESOLVED(
        "DSL_E795",
        "theme ''{0}'' 继承的父主题 ''{1}'' 未注册",
        Category.SEMANTIC,
        fixHint = "确认父主题文件存在，且文件名（去后缀）与 extends 值一致",
    ),

    /** 引用了未定义的 theme token（拼写错误或主题未声明该 token）。 */
    E796_UNKNOWN_THEME_TOKEN(
        "DSL_E796",
        "未定义的 theme token ''{0}''",
        Category.SEMANTIC,
        fixHint = "检查 token 名拼写；或在主题中声明该 token / 提供对应 seed 以派生",
    ),

    /** 颜色函数参数类型错误（颜色参数非颜色字面量，或数值参数非数字）。 */
    E797_COLOR_FN_ARG_TYPE(
        "DSL_E797",
        "颜色函数 ''{0}'' 参数类型错误：{1}",
        Category.SEMANTIC,
        fixHint = "颜色参数需为 #AARRGGBB/#RRGGBB 或解析为颜色的 token，数值参数需为 0..1 数字",
    ),

    /** 颜色函数名未知或参数个数不符。 */
    E798_COLOR_FN_UNKNOWN_OR_ARITY(
        "DSL_E798",
        "颜色函数 ''{0}'' 未知或参数个数不符：{1}",
        Category.SEMANTIC,
        fixHint = "可用：lighten(c,amt) / darken(c,amt) / alpha(c,a) / mix(c1,c2,t)",
    ),

    /** theme token 别名（引用）链存在循环。 */
    E799_THEME_TOKEN_ALIAS_CYCLE(
        "DSL_E799",
        "theme token ''{0}'' 的引用链存在循环",
        Category.SEMANTIC,
        fixHint = "检查 token 互相引用，移除循环",
    ),

    /** theme token 别名（引用）指向未定义的 token。 */
    E800_THEME_TOKEN_ALIAS_UNRESOLVED(
        "DSL_E800",
        "theme token ''{0}'' 引用的目标 token ''{1}'' 未定义",
        Category.SEMANTIC,
        fixHint = "检查被引用 token 名拼写，或在同一主题中声明该 token",
    ),

    // ===== 编译期 style-class 样式层 =====

    /** styles 块内规则选择器语法非法（缺少选择器、`.` 后缺类名等）。 */
    E801_STYLE_SELECTOR_INVALID(
        "DSL_E801",
        "styles 块内规则选择器非法：{0}",
        Category.PARSE,
        fixHint = "选择器形如 `button` / `.danger` / `button.primary` / `.danger.large`",
    ),

    // ===== FR-75 / UI DSL v2 基础诊断 =====

    AUI_V2_LEGACY_FORBIDS_V2_SYNTAX(
        "AUI-V2-001",
        "legacy 模式不允许使用 v2 语法：{0}",
        Category.PARSE,
    ),
    AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX(
        "AUI-V2-002",
        "strict 模式不允许使用 legacy 语法：{0}",
        Category.PARSE,
    ),
    AUI_V2_COMPONENT_MODEL_FORBIDS_GLOBAL_SCOPE(
        "AUI-V2-003",
        "component 的 model 仅允许 state/derived，不允许 {0}",
        Category.COMPONENT,
    ),
    AUI_V2_PARAM_READONLY(
        "AUI-V2-004",
        "params 是只读入参，不能赋值：{0}",
        Category.PARSE,
    ),
    AUI_V2_INVALID_NAMESPACE(
        "AUI-V2-005",
        "{0} namespace ''{1}'' 不合法，必须为 lowerCamelCase 静态标识符",
        Category.PARSE,
    ),
    AUI_V2_UNDECLARED_PARAM(
        "AUI-V2-006",
        "未声明的 params 字段 ''{0}''",
        Category.SEMANTIC,
    ),
    AUI_V2_LEGACY_DEPRECATED_SYNTAX(
        "AUI-V2-007",
        "legacy 语法 ''{0}'' 已废弃，请迁移到 {1}",
        Category.PARSE,
    ),
    AUI_V2_RESERVED_PREFIX(
        "AUI-V2-008",
        "标识符 ''{0}'' 使用了保留前缀，用户 DSL 不允许声明或访问该命名空间",
        Category.PARSE,
    ),
    AUI_V2_MODIFIER_BUNDLE_UNRESOLVED(
        "AUI-V2-009",
        "modifier bundle ''{0}'' 未在当前可见作用域声明",
        Category.SEMANTIC,
    ),
    AUI_V2_MODIFIER_BUNDLE_DUPLICATE(
        "AUI-V2-010",
        "modifier bundle ''{0}'' 在当前可见作用域重复声明",
        Category.SEMANTIC,
    ),
    AUI_V2_MODIFIER_BUNDLE_CYCLE(
        "AUI-V2-011",
        "modifier bundle 引用存在循环：{0}",
        Category.SEMANTIC,
    ),
    AUI_V2_DYNAMIC_USE_FORBIDDEN(
        "AUI-V2-012",
        "modifiers.use 只允许静态 bundle 标识符，不允许字符串或动态表达式",
        Category.PARSE,
    ),
    AUI_V2_UNKNOWN_COMPONENT_TOKEN(
        "AUI-V2-013",
        "组件 ''{0}'' 未声明 {1} token ''{2}''",
        Category.SEMANTIC,
    ),
    AUI_V2_STATIC_SYMBOL_REQUIRED(
        "AUI-V2-014",
        "''{0}'' 必须使用静态标识符，不能使用字符串或动态表达式",
        Category.PARSE,
    ),
    AUI_V2_PUBLIC_PROPS_SOFT_LIMIT(
        "AUI-V2-015",
        "组件 props 数量为 {0} 个，超过建议软上限 {1} 个，请拆分为 component tokens、model、data prop 或更小组件",
        Category.COMPONENT,
    ),
    AUI_V2_SHADER_LAYER_PROGRAM_REQUIRED(
        "AUI-V2-016",
        "shaderLayer 必须声明静态 program 符号",
        Category.PARSE,
    ),
    AUI_V2_SHADER_LAYER_PROGRAM_UNRESOLVED(
        "AUI-V2-017",
        "shaderLayer program ''{0}'' 未在 Shader Extension manifest 中声明",
        Category.SEMANTIC,
    ),
    AUI_V2_SHADER_INLINE_SOURCE_FORBIDDEN(
        "AUI-V2-018",
        ".allin 不允许内联 GLSL 源码；请把源码放入资源包并通过 manifest 注册 program",
        Category.PARSE,
    ),
    AUI_MG_ILLEGAL_NODE(
        "AUI-MG-001",
        "materialGraph 包含非法节点 ''{0}''，仅允许白名单材质节点",
        Category.SEMANTIC,
    ),
    AUI_MG_OVER_BUDGET(
        "AUI-MG-002",
        "materialGraph 成本预算超限：成本 {0} / 预算 {1}，节点 {2}，边 {3}",
        Category.SEMANTIC,
    ),
    AUI_MG_CYCLE(
        "AUI-MG-003",
        "materialGraph 存在循环依赖：{0}",
        Category.SEMANTIC,
    ),
    AUI_MG_STATIC_GRAPH_REQUIRED(
        "AUI-MG-004",
        "materialGraph.graph 必须是静态字符串，且包含静态节点图与 output 声明",
        Category.SEMANTIC,
    ),
    AUI_MG_STATIC_FALLBACK_REQUIRED(
        "AUI-MG-005",
        "materialGraph.fallback 必须是静态 fallback，允许值：{0}",
        Category.SEMANTIC,
    ),
    AUI_MG_TYPE_MISMATCH(
        "AUI-MG-006",
        "materialGraph 节点 ''{0}''（{1}）输入类型不匹配",
        Category.SEMANTIC,
    ),
    AUI_MG_UNKNOWN_DEPENDENCY(
        "AUI-MG-007",
        "materialGraph 节点 ''{0}'' 引用了未知依赖 ''{1}''",
        Category.SEMANTIC,
    ),
    AUI_MG_DUPLICATE_NODE(
        "AUI-MG-008",
        "materialGraph 包含重复节点 ''{0}''",
        Category.SEMANTIC,
    ),
    AUI_MG_DANGLING_OUTPUT(
        "AUI-MG-009",
        "materialGraph output 引用了未声明节点 ''{0}''",
        Category.SEMANTIC,
    ),
    AUI_MG_DYNAMIC_EXPRESSION(
        "AUI-MG-010",
        "materialGraph 节点 ''{0}'' 的参数 ''{1}'' 必须是静态参数值",
        Category.SEMANTIC,
    ),
    AUI_MG_INVALID_BUDGET(
        "AUI-MG-011",
        "materialGraph 预算 budget 必须是 1 到 {0} 的有限整数",
        Category.SEMANTIC,
    ),
    ;

    /** 错误码所属阶段类别，便于 IDE 高亮 / 报告分组。 */
    enum class Category { LEX, PARSE, SEMANTIC, COMPONENT, CODEGEN }

    /** 用 [args] 填充 [template] 占位符；无参时直接返回模板原文。 */
    fun format(vararg args: Any?): String =
        if (args.isEmpty()) template else formatTemplate(template, args)

    /**
     * 用 [args] 填充 [fixHint] 占位符；fixHint 未定义或为 null 时返回 null。
     *
     * 用于 M5-f 诊断渲染链路：Collector 在构造 Diagnostic 时调用本方法得到填充后的修复建议，
     * 渲染阶段再把结果拼入富格式输出。
     */
    fun formatFixHint(vararg args: Any?): String? {
        val hint = fixHint ?: return null
        // fixHint 不含 {N} 占位符时直接返回原文，避免占位替换误解析花括号
        // `\}` 必须转义：JVM 正则容忍裸 `}`，但 Kotlin/JS 的 Regex 走 unicode 模式，裸 `}` 被判为
        // 「孤立量词括号」抛 SyntaxError（FR-UI-065 浏览器编译带 fixHint 的 legacy 诊断时触发）
        if (!hint.contains(Regex("""\{\d+\}"""))) return hint
        if (args.isEmpty()) return hint
        return formatTemplate(hint, args)
    }

    companion object {
        /** 全集自检：构建期/单测调用，确保 [code] 字段在 enum 内全局唯一。 */
        fun assertNoDuplicateCodes() {
            val grouped = values().groupBy { it.code }
            val dupes = grouped.filter { it.value.size > 1 }
            require(dupes.isEmpty()) { "DslError code 重号: $dupes" }
        }
    }
}

/**
 * MessageFormat 平凡子集的平台中立填充（FR-UI-061 替代 java.text.MessageFormat，JVM-only）：
 * 支持 `{N}` 占位（参数 toString；越界保留原样，与 MessageFormat 一致）、`''` 单引号转义、
 * 以及 `'...'` quoted-literal 段（段内 `{` `}` 等原样输出，如 E792 fixHint 的 `'{'` `'}'`）。
 * 本文件模板集经核实不用 `{N,format}` 格式子句。
 * 与 MessageFormat 的唯一行为差异：数字参数不再按 locale 加千分位（诊断文本以 toString 原样呈现）；
 * JVM/JS 双端由同一实现保证逐字符一致。
 */
private fun formatTemplate(template: String, args: Array<out Any?>): String {
    val sb = StringBuilder(template.length + 16)
    var i = 0
    var quoted = false
    while (i < template.length) {
        val c = template[i]
        when {
            // ''（任何模式下）→ 字面单引号，优先于 quote 切换判定
            c == '\'' && i + 1 < template.length && template[i + 1] == '\'' -> {
                sb.append('\'')
                i += 2
            }
            // 单引号：切换 quoted-literal 模式，自身不输出
            c == '\'' -> {
                quoted = !quoted
                i++
            }
            // quote 段内：所有字符（含 { }）原样输出
            quoted -> {
                sb.append(c)
                i++
            }
            // {N} → args[N]（越界保留 {N} 原样）
            c == '{' -> {
                val end = template.indexOf('}', i + 1)
                val idx = if (end > i) template.substring(i + 1, end).toIntOrNull() else null
                when {
                    idx != null && idx in args.indices -> {
                        sb.append(args[idx].toString())
                        i = end + 1
                    }
                    idx != null -> {
                        sb.append(template, i, end + 1)
                        i = end + 1
                    }
                    else -> {
                        sb.append(c)
                        i++
                    }
                }
            }
            else -> {
                sb.append(c)
                i++
            }
        }
    }
    return sb.toString()
}
